import { FrontendAuthManager } from '../auth/frontend';
import { EmotionCategory, getDefaultEnabledEmotions, getEmotionsByCategories, getEmotionsByTypes } from '../config/emotions';
import { WidgetApiService } from '../services/api';
import { AuthUser, Comment, StarRatingStats, User, WidgetConfig } from '../types';
import { addStyles, createElement } from '../utils';
import { debounce, LazyLoader } from '../utils/performance';
import { CspUtils } from '../utils/security';
import { CommentForm } from './CommentForm';
import { CommentList } from './CommentList';
import { StarRatingDisplay, StarRatingDisplayMode } from './StarRatingDisplay';
import { StarRatingInput } from './StarRatingInput';

export class KomentasWidget {
  private container: HTMLElement;
  private config: WidgetConfig;
  private apiService: WidgetApiService;
  private authManager: FrontendAuthManager;
  private comments: Comment[] = [];
  private currentUser: AuthUser | null = null;
  private isLoading: boolean = false;
  private commentList: CommentList | null = null;
  private commentForm: CommentForm | null = null;
  private isDarkTheme: boolean = false;
  private articleId: string | null = null;
  private articleReactionsEnabled: boolean = false;
  private articleReactions: Map<string, { count: number; userReacted: boolean }> = new Map();
  private starRatingStats: StarRatingStats | null = null;
  private currentUserStarRating: number | null = null;

  // Performance optimizations
  private lazyLoader: LazyLoader;
  private debouncedRender: () => void;
  private commentCache = new Map<string, HTMLElement>();
  private intersectionObserver?: IntersectionObserver;

  constructor(container: HTMLElement | string, config: WidgetConfig) {
    // Validate required configuration
    if (!config.apiUrl) {
      throw new Error('apiUrl is required in widget configuration');
    }
    
    if (!config.projectId) {
      throw new Error('projectId is required in widget configuration');
    }
    
    // Validate maxCommentLength if provided
    if (config.maxCommentLength !== undefined && config.maxCommentLength <= 0) {
      throw new Error('maxCommentLength must be greater than 0');
    }
    
    // Validate sortBy if provided
    if (config.sortBy && !['newest', 'oldest', 'popular'].includes(config.sortBy)) {
      throw new Error('sortBy must be one of: newest, oldest, popular');
    }
    
    this.container = typeof container === 'string' 
      ? document.querySelector(container) as HTMLElement
      : container;
    
    if (!this.container) {
      throw new Error('Container element not found');
    }

    this.config = config;
    this.isDarkTheme = config.theme?.backgroundColor === '#1a1a1a' || false;

    // Log configuration for debugging
    console.log('[KomentasWidget] Constructor - Configuration received:');
    console.log('[KomentasWidget] - projectId:', config.projectId);
    console.log('[KomentasWidget] - enableCommentStarRatings:', config.enableCommentStarRatings);
    console.log('[KomentasWidget] - allowCommentStarRatingUpdate:', config.allowCommentStarRatingUpdate);
    console.log('[KomentasWidget] - enableReactions:', config.enableReactions);
    console.log('[KomentasWidget] - Full config:', JSON.stringify(config, null, 2));

    // Initialize performance optimizations
    this.lazyLoader = new LazyLoader({
      rootMargin: '50px',
      threshold: 0.1
    });
    
    this.debouncedRender = debounce(() => this.render(), 100);
    
    // Set up intersection observer for image lazy loading
    this.setupIntersectionObserver();

    this.apiService = new WidgetApiService(this.config.apiUrl);
    this.authManager = new FrontendAuthManager(
      this.config.apiUrl, 
      this.config.portalUrl,
      this.config.oauthProviders
    );
    
    // Set up unauthorized callback to handle expired tokens
    this.apiService.setOnUnauthorized(() => {
      console.log('Token expired, logging out...');
      this.authManager.logout();
      this.currentUser = null;
      this.render();
    });
    
    // Subscribe to auth state changes
    this.authManager.subscribe(async (authState) => {
      if (authState.isAuthenticated && authState.user && authState.token) {
        // User logged in
        
        this.currentUser = {
          id: authState.user.id,
          displayName: authState.user.name,
          email: authState.user.email,
          avatarUrl: authState.user.avatar,
          isAnonymous: false
        };
        
        // Set token and user data in API service
        this.apiService.setToken(
          authState.token,
          authState.user.provider,
          authState.user.email,
          authState.user.name,
          authState.user.avatar
        );
        
        // Reload article reactions to fetch user's existing ratings/reactions
        if (this.articleId) {
          await this.loadArticleReactions();
        }

        // Reload comment star ratings to fetch user's ratings
        if (this.config.enableCommentStarRatings && this.comments.length > 0) {
          await this.loadCommentStarRatings();
        }
        
        this.render();
      } else if (!authState.isAuthenticated && this.currentUser) {
        // User logged out
        this.currentUser = null;
        this.apiService.clearToken();
        
        // Clear user-specific data
        this.currentUserStarRating = null;

        // Clear user ratings from comment star ratings
        this.comments.forEach(comment => {
          if (comment.starRating) {
            comment.starRating.userRating = null;
          }
        });
        
        this.render();
      }
    });
    
    this.init();
  }

  private async init(): Promise<void> {
    try {
      // Check for CSP issues
      CspUtils.warnIfCspIssues();
      
      this.addStyles();
      this.createLayout();
      
      // Check for existing authentication from auth manager
      const authState = this.authManager.getState();
      
      if (authState.isAuthenticated && authState.user && authState.token) {
        // Convert WidgetUser to AuthUser format
        this.currentUser = {
          id: authState.user.id,
          displayName: authState.user.name,
          email: authState.user.email,
          avatarUrl: authState.user.avatar,
          isAnonymous: false
        };
        
        // Set token and user data in API service
        this.apiService.setToken(
          authState.token,
          authState.user.provider,
          authState.user.email,
          authState.user.name,
          authState.user.avatar
        );
      }

      await this.loadComments();
      this.render();
    } catch (error) {
      console.error('❌ Error initializing Komentas widget:', error);
      this.showError('Failed to load comments');
    }
  }

  private addStyles(): void {
    const isDark = this.isDarkTheme;
    
    const styles = `
      .komentas-widget {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        max-width: 800px;
        margin: 0 auto;
        padding: 20px;
        background: ${isDark ? '#1a1a1a' : '#ffffff'};
        color: ${isDark ? '#ffffff' : '#000000'};
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .komentas-widget * {
        box-sizing: border-box;
      }

      .komentas-header {
        margin-bottom: 24px;
        padding-bottom: 16px;
        border-bottom: 1px solid ${isDark ? '#333' : '#e1e5e9'};
      }

      .komentas-title {
        font-size: 18px;
        font-weight: 600;
        margin: 0 0 8px 0;
      }

      .komentas-count {
        font-size: 14px;
        color: ${isDark ? '#888' : '#666'};
        margin: 0;
      }

      .komentas-auth-section {
        margin-bottom: 24px;
        padding: 16px;
        background: ${isDark ? '#2a2a2a' : '#f8f9fa'};
        border-radius: 6px;
      }

      .komentas-user-info {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
      }

      .komentas-avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: ${isDark ? '#444' : '#ddd'};
      }

      .komentas-user-name {
        font-weight: 500;
      }

      .komentas-logout-btn {
        background: none;
        border: 1px solid ${isDark ? '#555' : '#ddd'};
        color: ${isDark ? '#ccc' : '#666'};
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
        margin-left: auto;
      }

      .komentas-logout-btn:hover {
        background: ${isDark ? '#333' : '#f0f0f0'};
      }

      .komentas-error {
        background: #fee;
        color: #c33;
        padding: 12px;
        border-radius: 4px;
        margin-bottom: 16px;
        border: 1px solid #fcc;
      }

      .komentas-success {
        background: #efe;
        color: #363;
        padding: 12px;
        border-radius: 4px;
        margin-bottom: 16px;
        border: 1px solid #cfc;
      }

      .komentas-loading {
        text-align: center;
        padding: 20px;
        color: ${isDark ? '#888' : '#666'};
      }

      .komentas-empty {
        text-align: center;
        padding: 40px 20px;
        color: ${isDark ? '#888' : '#666'};
      }

      .komentas-empty h3 {
        margin: 0 0 8px 0;
        font-size: 16px;
      }

      .komentas-empty p {
        margin: 0;
        font-size: 14px;
      }

      .komentas-oauth-section {
        padding: 20px;
        border-radius: 8px;
        background: ${isDark ? '#2a2a2a' : '#f9f9f9'};
        border: 1px solid ${isDark ? '#404040' : '#e0e0e0'};
      }

      .komentas-oauth-title {
        margin: 0 0 16px 0;
        font-size: 16px;
        font-weight: 600;
        text-align: center;
        color: ${isDark ? '#fff' : '#333'};
      }

      .komentas-oauth-btn {
        display: block;
        width: 100%;
        padding: 12px 16px;
        margin-bottom: 8px;
        border: 1px solid ${isDark ? '#404040' : '#d0d0d0'};
        border-radius: 6px;
        background: ${isDark ? '#333' : '#fff'};
        color: ${isDark ? '#fff' : '#333'};
        font-size: 14px;
        font-weight: 500;
        text-align: center;
        cursor: pointer;
        transition: all 0.2s ease;
        text-decoration: none;
      }

      .komentas-oauth-btn:hover {
        background: ${isDark ? '#404040' : '#f5f5f5'};
        border-color: ${isDark ? '#505050' : '#b0b0b0'};
        transform: translateY(-1px);
      }

      .komentas-oauth-btn:active {
        transform: translateY(0);
      }

      .komentas-oauth-google {
        border-color: #4285f4;
        color: #4285f4;
      }

      .komentas-oauth-google:hover {
        background: #4285f4;
        color: white;
      }

      .komentas-oauth-github {
        border-color: #333;
        color: #333;
      }

      .komentas-oauth-github:hover {
        background: #333;
        color: white;
      }

      .komentas-oauth-microsoft-entra-id {
        border-color: #0078d4;
        color: #0078d4;
      }

      .komentas-oauth-microsoft-entra-id:hover {
        background: #0078d4;
        color: white;
      }

      .komentas-oauth-apple {
        border-color: #000;
        color: #000;
      }

      .komentas-oauth-apple:hover {
        background: #000;
        color: white;
      }

      .komentas-oauth-linkedin {
        border-color: #0077b5;
        color: #0077b5;
      }

      .komentas-oauth-linkedin:hover {
        background: #0077b5;
        color: white;
      }

      .komentas-signin-prompt {
        margin-top: 32px;
        padding: 24px;
        border-radius: 8px;
        background: ${isDark ? '#2a2a2a' : '#f9f9f9'};
        border: 1px solid ${isDark ? '#404040' : '#e0e0e0'};
        text-align: center;
      }

      .komentas-signin-message {
        margin: 0 0 16px 0;
        font-size: 15px;
        font-weight: 500;
        color: ${isDark ? '#ccc' : '#555'};
      }

      .komentas-signin-buttons {
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-width: 300px;
        margin: 0 auto;
      }

      .komentas-footer {
        margin-top: 24px;
        padding-top: 16px;
        border-top: 1px solid ${isDark ? '#333' : '#e0e0e0'};
        text-align: center;
      }

      .komentas-branding {
        font-size: 12px;
        color: ${isDark ? '#888' : '#999'};
      }

      .komentas-branding-link {
        color: ${isDark ? '#667eea' : '#5568d3'};
        text-decoration: none;
        font-weight: 500;
        transition: color 0.2s ease;
      }

      .komentas-branding-link:hover {
        color: ${isDark ? '#7c8ef5' : '#4456b3'};
        text-decoration: underline;
      }

      /* Force article reaction buttons to not dim when disabled */
      .komentas-article-reaction-btn:disabled {
        opacity: 1 !important;
        -webkit-text-fill-color: currentColor !important;
        color: inherit !important;
      }

      .komentas-article-reaction-btn[disabled] {
        opacity: 1 !important;
        -webkit-text-fill-color: currentColor !important;
        color: inherit !important;
      }
    `;

    addStyles(styles, 'komentas-widget-styles');
  }

  private createLayout(): void {
    this.container.className = 'komentas-widget';
    this.container.innerHTML = '';

    // Header - only show in regular mode, not in reactions-only mode
    if (!this.config.reactionsOnlyMode) {
      const header = createElement('div', { className: 'komentas-header' }, [
        createElement('h2', { className: 'komentas-title' }, ['Comments']),
        createElement('p', { className: 'komentas-count' }, ['0 comments'])
      ]);
      this.container.appendChild(header);
    }

    // Auth section
    const authSection = createElement('div', { className: 'komentas-auth-section' });

    // Comments section
    const commentsSection = createElement('div', { className: 'komentas-comments' });

    this.container.appendChild(authSection);
    this.container.appendChild(commentsSection);
    
    // Footer with branding (optional)
    if (this.config.showBranding !== false) {
      const footer = this.createFooter();
      this.container.appendChild(footer);
    }
  }

  private async loadComments(): Promise<void> {
    try {
      this.setLoading(true);
      
      const articleUrl = this.config.articleUrl || window.location.href;
      
      // Create or get article
      const article = await this.apiService.createOrGetArticle({
        url: articleUrl,
        title: this.config.articleTitle || document.title,
        projectId: this.config.projectId
      });

      // Store article ID for reactions
      this.articleId = article.id;
      this.articleReactionsEnabled = article.reactionsEnabled;

      // Load comments for article with project ID for security isolation
      const commentsResponse = await this.apiService.getComments(article.url, this.config.projectId);
      
      this.comments = commentsResponse.data;

      // Load article reactions if widget is in reactions-only mode, article reactions are enabled, showArticleReactions config is true, or star ratings are enabled
      const shouldLoadReactions = this.config.reactionsOnlyMode || article.reactionsEnabled || this.config.showArticleReactions || this.config.enableStarRatings;
      console.log('[STAR-DEBUG] shouldLoadReactions:', shouldLoadReactions, {
        reactionsOnlyMode: this.config.reactionsOnlyMode,
        articleReactionsEnabled: article.reactionsEnabled,
        showArticleReactions: this.config.showArticleReactions,
        enableStarRatings: this.config.enableStarRatings
      });
      
      if (shouldLoadReactions) {
        await this.loadArticleReactions();
      }

      // Load comment star ratings if enabled
      if (this.config.enableCommentStarRatings) {
        await this.loadCommentStarRatings();
      }
    } catch (error) {
      console.error('Error loading comments:', error);
      this.showError('Failed to load comments');
    } finally {
      this.setLoading(false);
      // Render the widget to display the loaded comments
      this.render();
    }
  }

  private updateHeader(): void {
    const countElement = this.container.querySelector('.komentas-count') as HTMLElement;

    if (countElement) {
      const count = this.comments.length;
      countElement.textContent = `${count} comment${count !== 1 ? 's' : ''}`;
    }
  }

  private renderAuth(): void {
    const authSection = this.container.querySelector('.komentas-auth-section') as HTMLElement;
    authSection.innerHTML = '';

    if (this.currentUser) {
      // Make sure auth section is visible
      authSection.style.display = '';
      
      // Generate fallback avatar URL if none provided
      const avatarUrl = this.currentUser.avatarUrl 
        ? this.currentUser.avatarUrl 
        : `https://ui-avatars.com/api/?name=${encodeURIComponent(this.currentUser.displayName)}&size=32`;
      
      // Build user info children elements
      const userInfoChildren: (HTMLElement | string)[] = [
        createElement('img', { 
          className: 'komentas-avatar',
          src: avatarUrl,
          alt: this.currentUser.displayName
        }),
        createElement('span', { className: 'komentas-user-name' }, [this.currentUser.displayName])
      ];
      
      // Conditionally add logout button based on config (default: true)
      const showLogoutButton = this.config.showLogoutButton !== false;
      if (showLogoutButton) {
        userInfoChildren.push(
          createElement('button', { 
            className: 'komentas-logout-btn',
            type: 'button'
          }, ['Logout'])
        );
      }
      
      // User is logged in - show user info and comment form at the top
      const userInfo = createElement('div', { className: 'komentas-user-info' }, userInfoChildren);

      // Add logout button event listener if button was created
      if (showLogoutButton) {
        const logoutBtn = userInfo.querySelector('.komentas-logout-btn') as HTMLButtonElement;
        logoutBtn.addEventListener('click', () => this.logout());
      }

      authSection.appendChild(userInfo);

      // Determine if we should show article reactions (including star ratings)
      const shouldShowArticleReactions = this.config.reactionsOnlyMode || this.config.showArticleReactions || this.articleReactionsEnabled || this.config.enableStarRatings;
      console.log('[STAR-DEBUG] Authenticated - shouldShowArticleReactions:', shouldShowArticleReactions, {
        reactionsOnlyMode: this.config.reactionsOnlyMode,
        showArticleReactions: this.config.showArticleReactions,
        articleReactionsEnabled: this.articleReactionsEnabled,
        enableStarRatings: this.config.enableStarRatings,
        starRatingStats: this.starRatingStats
      });

      // Add article reactions FIRST (before comment form) if enabled
      if (shouldShowArticleReactions && !this.config.reactionsOnlyMode) {
        console.log('[STAR-DEBUG] Creating article reactions for authenticated user');
        const articleReactions = this.createArticleReactions();
        authSection.appendChild(articleReactions);
      }

      // Create comment form ONLY if NOT in reactions-only mode
      if (!this.config.reactionsOnlyMode) {
        if (!this.commentForm) {
          this.commentForm = new CommentForm({
            onSubmit: (content: string) => this.submitComment(content),
            maxLength: this.config.maxCommentLength || 500,
            theme: this.isDarkTheme ? 'dark' : 'light'
          });
        }

        authSection.appendChild(this.commentForm.getElement());
      } else {
        // In reactions-only mode, show a message and article reactions
        const reactionsOnlyMessage = createElement('div', { 
          className: 'komentas-reactions-only-message',
          style: 'padding: 12px; background: #f0f9ff; border: 1px solid #3b82f6; border-radius: 6px; margin-top: 10px; color: #1e40af; font-size: 14px;'
        }, ['💙 Reactions-Only Mode: You can react to this article, but text comments are disabled.']);
        
        authSection.appendChild(reactionsOnlyMessage);
        
        // Add article reactions
        const articleReactions = this.createArticleReactions();
        authSection.appendChild(articleReactions);
      }

    } else {
      // Determine if we should show article reactions for unauthenticated users (including star ratings)
      const shouldShowArticleReactions = this.config.reactionsOnlyMode || this.config.showArticleReactions || this.articleReactionsEnabled || this.config.enableStarRatings;
      console.log('[STAR-DEBUG] Unauthenticated - shouldShowArticleReactions:', shouldShowArticleReactions, {
        reactionsOnlyMode: this.config.reactionsOnlyMode,
        showArticleReactions: this.config.showArticleReactions,
        articleReactionsEnabled: this.articleReactionsEnabled,
        enableStarRatings: this.config.enableStarRatings,
        starRatingStats: this.starRatingStats
      });
      
      // Not logged in
      if (this.config.reactionsOnlyMode) {
        // In reactions-only mode, show article reactions first, then OAuth login buttons
        authSection.style.display = '';
        
        // Show article reactions first (disabled for non-authenticated users)
        const articleReactions = this.createArticleReactions();
        authSection.appendChild(articleReactions);
        
        // Then show OAuth login buttons below
        const signInMessage = createElement('div', {
          className: 'komentas-oauth-section',
          style: 'margin-top: 20px;'
        }, [
          createElement('h3', {
            className: 'komentas-oauth-title'
          }, ['Sign in to react to this article']),
          createElement('div', {
            className: 'komentas-oauth-buttons'
          })
        ]);

        const buttonsContainer = signInMessage.querySelector('.komentas-oauth-buttons') as HTMLElement;
        
        // Render OAuth provider buttons
        const providers = this.getAvailableProviders();
        providers.forEach(provider => {
          const button = createElement('button', {
            className: `komentas-oauth-btn komentas-oauth-${provider.id}`,
            'data-provider': provider.id
          }, [`${provider.icon} Sign in with ${provider.name}`]);

          button.addEventListener('click', () => this.loginWithProvider(provider.id));
          buttonsContainer.appendChild(button);
        });

        authSection.appendChild(signInMessage);
      } else if (!shouldShowArticleReactions) {
        // Not in reactions-only mode AND article reactions not enabled - hide auth section, show sign-in prompt at bottom of comments
        authSection.style.display = 'none';
      } else {
        // Article reactions enabled in full mode - show only reactions, NO login buttons here
        // Login buttons will be shown at the bottom after comments
        authSection.style.display = '';
        const articleReactions = this.createArticleReactions();
        authSection.appendChild(articleReactions);
        console.log('[STAR-DEBUG] Appended articleReactions to authSection', {
          authSectionChildren: authSection.children.length,
          authSectionDisplay: authSection.style.display,
          articleReactionsChildren: articleReactions.children.length,
          authSectionInDOM: document.body.contains(authSection)
        });
      }
    }
  }

  private renderComments(): void {
    const commentsSection = this.container.querySelector('.komentas-comments') as HTMLElement;
    
    if (!commentsSection) {
      console.error('Comments section not found in DOM!');
      return;
    }
    
    commentsSection.innerHTML = '';

    // In reactions-only mode, hide the comments section entirely
    if (this.config.reactionsOnlyMode) {
      commentsSection.style.display = 'none';
      return;
    }

    // Make sure comments section is visible in regular mode
    commentsSection.style.display = '';

    if (this.isLoading) {
      const loading = createElement('div', { className: 'komentas-loading' }, ['Loading comments...']);
      commentsSection.appendChild(loading);
      return;
    }

    if (this.comments.length === 0) {
      const empty = createElement('div', { className: 'komentas-empty' }, [
        createElement('h3', {}, ['No comments yet']),
        createElement('p', {}, ['Be the first to leave a comment!'])
      ]);
      commentsSection.appendChild(empty);
      
      // Show sign-in prompt if not logged in
      if (!this.currentUser) {
        this.appendSignInPrompt(commentsSection);
      }
      return;
    }

    // Sort comments
    const sortedComments = this.sortComments([...this.comments]);

    console.log('[KomentasWidget] Creating/updating CommentList');
    console.log('[KomentasWidget] Config enableCommentStarRatings:', this.config.enableCommentStarRatings);
    console.log('[KomentasWidget] Config allowCommentStarRatingUpdate:', this.config.allowCommentStarRatingUpdate);
    console.log('[KomentasWidget] Number of comments:', sortedComments.length);
    console.log('[KomentasWidget] Comments with starRating:', sortedComments.filter(c => c.starRating).length);

    // Create comment list
    if (!this.commentList) {
      console.log('[KomentasWidget] Creating new CommentList instance');
      this.commentList = new CommentList({
        comments: sortedComments,
        currentUser: this.currentUser,
        widgetConfig: this.config,
        onReply: (parentId: string, content: string) => this.submitReply(parentId, content),
        onReact: (commentId: string, type: string) => this.toggleReaction(commentId, type),
        onEdit: (commentId: string, content: string) => this.editComment(commentId, content),
        onDelete: (commentId: string) => this.deleteComment(commentId),
        onCommentStarRate: (commentId: string, rating: number) => this.handleCommentStarRatingSubmit(commentId, rating),
        onCommentStarClear: (commentId: string) => this.handleCommentStarRatingClear(commentId),
        onLoginRequired: () => {
          const providers = this.getAvailableProviders();
          if (providers.length > 0) {
            this.loginWithProvider(providers[0].id);
          } else {
            this.showError('Please sign in to rate comments.');
          }
        },
        enableReactions: this.config.enableReactions || true,
        enableCommentStarRatings: this.config.enableCommentStarRatings || false,
        allowCommentStarRatingUpdate: this.config.allowCommentStarRatingUpdate,
        availableEmotions: this.config.availableEmotions,
        emotionCategories: this.config.emotionCategories,
        theme: this.isDarkTheme ? 'dark' : 'light'
      });
      console.log('[KomentasWidget] CommentList created');
    } else {
      console.log('[KomentasWidget] Updating existing CommentList');
      this.commentList.updateComments(sortedComments);
      this.commentList.updateCurrentUser(this.currentUser);
    }

    const commentListElement = this.commentList.getElement();
    commentsSection.appendChild(commentListElement);
    
    // Show sign-in prompt at bottom if not logged in
    if (!this.currentUser) {
      this.appendSignInPrompt(commentsSection);
    }
  }

  private appendSignInPrompt(container: HTMLElement): void {
    const signInPrompt = createElement('div', {
      className: 'komentas-signin-prompt'
    }, [
      createElement('p', {
        className: 'komentas-signin-message'
      }, ['Want to join the conversation?']),
      createElement('div', {
        className: 'komentas-signin-buttons'
      })
    ]);

    const buttonsContainer = signInPrompt.querySelector('.komentas-signin-buttons') as HTMLElement;
    
    // Render OAuth provider buttons
    const providers = this.getAvailableProviders();
    providers.forEach(provider => {
      const button = createElement('button', {
        className: `komentas-oauth-btn komentas-oauth-${provider.id}`,
        'data-provider': provider.id
      }, [`${provider.icon} Sign in with ${provider.name}`]);

      button.addEventListener('click', () => this.loginWithProvider(provider.id));
      buttonsContainer.appendChild(button);
    });

    container.appendChild(signInPrompt);
  }

  private createFooter(): HTMLElement {
    const footer = createElement('div', {
      className: 'komentas-footer'
    }, [
      createElement('div', {
        className: 'komentas-branding'
      }, [
        'Powered by ',
        createElement('a', {
          href: 'https://komentas.com',
          target: '_blank',
          rel: 'noopener noreferrer',
          className: 'komentas-branding-link'
        }, ['Komentas'])
      ])
    ]);
    
    return footer;
  }

  /**
   * Create article reactions component for reactions-only mode
   */
  private createArticleReactions(): HTMLElement {
    console.log('[STAR-DEBUG] createArticleReactions called', {
      enableStarRatings: this.config.enableStarRatings,
      starRatingStats: this.starRatingStats,
      enableReactions: this.config.enableReactions
    });
    
    const container = createElement('div', { 
      className: 'komentas-article-reactions',
      style: 'margin-top: 20px; padding: 20px; background: white; border: 1px solid #e5e7eb; border-radius: 8px;'
    });

    // Add star rating section if enabled
    if (this.config.enableStarRatings && this.starRatingStats) {
      console.log('[STAR-DEBUG] Creating star rating section');
      const starRatingSection = this.createStarRatingSection();
      container.appendChild(starRatingSection);
      
      // Add separator if showing emotions too
      if (this.config.enableReactions !== false) {
        const separator = createElement('hr', {
          style: 'margin: 20px 0; border: none; border-top: 1px solid #e5e7eb;'
        });
        container.appendChild(separator);
      }
    } else {
      console.log('[STAR-DEBUG] NOT creating star rating section', {
        enableStarRatings: this.config.enableStarRatings,
        starRatingStats: this.starRatingStats
      });
    }

    // Only show emotion reactions if not disabled
    if (this.config.enableReactions !== false) {
      const title = createElement('h3', { 
        style: 'margin: 0 0 15px 0; font-size: 16px; font-weight: 600; color: #374151;'
      }, ['React to this article']);

      const reactionsSection = createElement('div', { 
        className: 'komentas-article-reactions-buttons',
        style: 'display: flex; gap: 10px; flex-wrap: wrap;'
      });

      // Determine which emotions to display based on configuration
      let emotions;
      
      if (this.config.availableEmotions && this.config.availableEmotions.length > 0) {
        // Option 1: Whitelist approach - specific emotion types
        emotions = getEmotionsByTypes(this.config.availableEmotions);
      } else if (this.config.emotionCategories && this.config.emotionCategories.length > 0) {
        // Option 2: Category approach - emotions by categories
        emotions = getEmotionsByCategories(this.config.emotionCategories as EmotionCategory[]);
      } else {
        // Option 3: Default - use emotions marked as defaultEnabled (like, love, laugh, happy)
        emotions = getDefaultEnabledEmotions();
      }

      const isAuthenticated = !!this.currentUser;

      emotions.forEach(emotion => {
        // Get existing reaction data if available
        const reactionData = this.articleReactions.get(emotion.type) || { count: 0, userReacted: false };
        
        const btnAttributes: any = {
          className: 'komentas-article-reaction-btn',
          type: 'button',
          'data-type': emotion.type,
          title: isAuthenticated ? emotion.label : 'Sign in to react',
          style: `padding: 10px 16px; font-size: 20px; background: #f3f4f6; border: 2px solid #e5e7eb; border-radius: 8px; cursor: ${isAuthenticated ? 'pointer' : 'not-allowed'}; transition: all 0.2s; display: flex; align-items: center; gap: 6px; opacity: 1 !important;`
        };

        if (!isAuthenticated) {
          btnAttributes.disabled = 'disabled';
        }
        
        const btn = createElement('button', btnAttributes, [emotion.emoji, ` ${reactionData.count}`]);

        // Set initial active state if user has reacted
        if (reactionData.userReacted && isAuthenticated) {
          btn.classList.add('active');
          btn.style.background = '#dbeafe';
          btn.style.borderColor = '#3b82f6';
        }

        // Add hover effect only for authenticated users
        if (isAuthenticated) {
          btn.addEventListener('mouseenter', () => {
            btn.style.background = '#e5e7eb';
            btn.style.borderColor = '#3b82f6';
            btn.style.transform = 'scale(1.05)';
          });
          
          btn.addEventListener('mouseleave', () => {
            const currentBg = reactionData.userReacted ? '#dbeafe' : '#f3f4f6';
            const currentBorder = reactionData.userReacted ? '#3b82f6' : '#e5e7eb';
            btn.style.background = currentBg;
            btn.style.borderColor = currentBorder;
            btn.style.transform = 'scale(1)';
          });

          // Add click handler for authenticated users
          btn.addEventListener('click', () => {
            console.log(`Article reaction: ${emotion.type}`);
            this.toggleArticleReaction(emotion.type);
          });
        }

        reactionsSection.appendChild(btn);
      });

      container.appendChild(title);
      container.appendChild(reactionsSection);
    }

    return container;
  }

  /**
   * Create star rating section
   */
  private createStarRatingSection(): HTMLElement {
    console.log('[STAR-DEBUG] createStarRatingSection called', {
      starRatingStats: this.starRatingStats,
      currentUser: this.currentUser,
      displayMode: this.config.starRatingDisplayMode,
      allowUpdate: this.config.allowStarRatingUpdate
    });
    
    const section = createElement('div', {
      className: 'komentas-star-rating-section'
    });

    const isAuthenticated = !!this.currentUser;
    const displayMode: StarRatingDisplayMode = this.config.starRatingDisplayMode || 'medium';
    const allowUpdate = this.config.allowStarRatingUpdate !== false;

    // Always show star rating display (even with 0 ratings)
    if (this.starRatingStats) {
      console.log('[STAR-DEBUG] Creating StarRatingDisplay component', {
        totalCount: this.starRatingStats.totalCount
      });
      const displayComponent = new StarRatingDisplay(this.starRatingStats, {
        mode: displayMode,
        showDistribution: displayMode === 'large'
      });
      
      const displayElement = displayComponent.getElement();
      section.appendChild(displayElement);
    }

    // Show interactive input if user is authenticated (or placeholder if not)
    if (isAuthenticated) {
      console.log('[STAR-DEBUG] User is authenticated, showing input or message');
      // Only show input if user hasn't rated yet, or if updates are allowed
      const canRate = !this.currentUserStarRating || allowUpdate;
      
      if (canRate) {
        console.log('[STAR-DEBUG] Creating StarRatingInput component');
        const inputComponent = new StarRatingInput({
          currentRating: this.currentUserStarRating,
          isAuthenticated: true,
          allowUpdate: allowUpdate,
          onRatingSelect: async (rating: number) => {
            await this.submitStarRating(rating);
          },
          onRatingClear: async () => {
            await this.clearStarRating();
          }
        });
        
        const inputElement = inputComponent.getElement();
        section.appendChild(inputElement);
      } else {
        console.log('[STAR-DEBUG] User already rated, showing message');
        // User has already rated and updates are not allowed
        const message = createElement('p', {
          style: 'margin-top: 10px; font-size: 14px; color: #6b7280;'
        }, [`You rated this article ${this.currentUserStarRating} star${this.currentUserStarRating !== 1 ? 's' : ''}.`]);
        section.appendChild(message);
      }
    } else {
      console.log('[STAR-DEBUG] User not authenticated, showing login prompt');
      // Show login prompt for unauthenticated users
      const loginPrompt = createElement('div', {
        style: 'margin-top: 10px; padding: 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px;'
      });
      
      const promptText = createElement('p', {
        style: 'margin: 0 0 8px 0; font-size: 14px; color: #6b7280;'
      }, ['Sign in to rate this article']);
      
      loginPrompt.appendChild(promptText);
      section.appendChild(loginPrompt);
    }

    console.log('[STAR-DEBUG] Star rating section created, children:', section.children.length);
    return section;
  }

  /**
   * Submit a star rating for the current article
   */
  private async submitStarRating(rating: number): Promise<void> {
    if (!this.currentUser) {
      console.warn('User must be logged in to submit star rating');
      return;
    }

    if (!this.articleId) {
      console.error('Article ID not available');
      return;
    }

    try {
      // Call API to submit star rating
      await this.apiService.submitStarRating(this.articleId, rating);
      
      console.log(`Star rating submitted: ${rating}`);

      // Update local state
      this.currentUserStarRating = rating;

      // Reload article reactions to get updated statistics
      await this.loadArticleReactions();

      // Re-render the widget to show updated UI
      this.render();
    } catch (error) {
      console.error('Failed to submit star rating:', error);
      // Optionally show error to user
      alert('Failed to submit rating. Please try again.');
    }
  }

  /**
   * Clear the user's star rating for the current article
   */
  private async clearStarRating(): Promise<void> {
    if (!this.currentUser) {
      console.warn('User must be logged in to clear star rating');
      return;
    }

    if (!this.articleId) {
      console.error('Article ID not available');
      return;
    }

    if (!this.currentUserStarRating) {
      console.warn('No star rating to clear');
      return;
    }

    try {
      // Call API to delete star rating
      await this.apiService.deleteStarRating(this.articleId, this.currentUserStarRating, this.config.projectId);
      
      console.log(`Star rating cleared: ${this.currentUserStarRating}`);

      // Update local state
      this.currentUserStarRating = null;

      // Reload article reactions to get updated statistics
      await this.loadArticleReactions();

      // Re-render the widget to show updated UI
      this.render();
    } catch (error) {
      console.error('Failed to clear star rating:', error);
      // Optionally show error to user
      alert('Failed to clear rating. Please try again.');
    }
  }

  /**
   * Toggle article reaction (placeholder for now)
   */
  private async toggleArticleReaction(reactionType: string): Promise<void> {
    if (!this.currentUser) {
      console.warn('User must be logged in to react');
      return;
    }

    if (!this.articleId) {
      console.error('Article ID not available');
      return;
    }

    try {
      // Call API to toggle reaction
      const result = await this.apiService.toggleArticleReaction(this.articleId, reactionType);
      
      console.log('Article reaction toggled:', result);

      // Update local state based on result
      const reactionData = this.articleReactions.get(reactionType) || { count: 0, userReacted: false };
      
      // Toggle the user reaction state
      if (reactionData.userReacted) {
        reactionData.count = Math.max(0, reactionData.count - 1);
        reactionData.userReacted = false;
      } else {
        reactionData.count += 1;
        reactionData.userReacted = true;
      }
      
      this.articleReactions.set(reactionType, reactionData);

      // Update UI
      this.updateArticleReactionButton(reactionType);
    } catch (error) {
      console.error('Failed to toggle article reaction:', error);
      // Optionally show error to user
    }
  }

  /**
   * Load article reactions from the API
   */
  private async loadArticleReactions(): Promise<void> {
    console.log('[STAR-DEBUG] loadArticleReactions called', { articleId: this.articleId });
    
    if (!this.articleId) {
      console.warn('Article ID not available for loading reactions');
      return;
    }

    try {
      const response = await this.apiService.getArticleReactions(this.articleId, this.config.projectId);
      console.log('[STAR-DEBUG] API response:', response);
      
      // Clear existing reactions
      this.articleReactions.clear();

      // Process reactions data
      if (response.reactions && Array.isArray(response.reactions)) {
        response.reactions.forEach((reaction: any) => {
          this.articleReactions.set(reaction.type, {
            count: reaction.count || 0,
            userReacted: reaction.userReacted || false
          });
        });
      }

      // Process star rating data if available
      if (response.starRating) {
        console.log('[STAR-DEBUG] Setting starRatingStats:', response.starRating);
        this.starRatingStats = {
          average: response.starRating.average,
          totalCount: response.starRating.totalCount || 0,
          distribution: response.starRating.distribution || {}
        };
        
        // Find user's current star rating if they've rated
        if (this.currentUser && response.reactions) {
          const userStarReaction = response.reactions.find((r: any) => 
            r.userReacted && r.type.toLowerCase().startsWith('star')
          );
          
          if (userStarReaction) {
            // Extract star value from type name (e.g., "star3" -> 3)
            const starMatch = userStarReaction.type.match(/star(\d)/i);
            
            if (starMatch) {
              this.currentUserStarRating = parseInt(starMatch[1], 10);
            } else {
              this.currentUserStarRating = null;
            }
          } else {
            // No star rating found - user hasn't rated or has cleared their rating
            this.currentUserStarRating = null;
          }
        } else {
          // No user or no reactions data
          this.currentUserStarRating = null;
        }
      } else {
        console.log('[STAR-DEBUG] No starRating in response, setting to null');
        this.starRatingStats = null;
        this.currentUserStarRating = null;
      }

      console.log('[STAR-DEBUG] Final starRatingStats:', this.starRatingStats);
      console.log('[STAR-DEBUG] Final currentUserStarRating:', this.currentUserStarRating);

      // Update all reaction buttons if they exist
      if (this.config.reactionsOnlyMode) {
        this.updateAllArticleReactionButtons();
      }
    } catch (error) {
      console.error('Failed to load article reactions:', error);
    }
  }

  /**
   * Update a single article reaction button UI
   */
  private updateArticleReactionButton(reactionType: string): void {
    const btn = this.container.querySelector(`[data-type="${reactionType}"]`) as HTMLButtonElement;
    
    if (!btn) return;

    const reactionData = this.articleReactions.get(reactionType) || { count: 0, userReacted: false };
    
    // Determine which emotions to display based on configuration
    let emotions;
    if (this.config.availableEmotions && this.config.availableEmotions.length > 0) {
      emotions = getEmotionsByTypes(this.config.availableEmotions);
    } else if (this.config.emotionCategories && this.config.emotionCategories.length > 0) {
      emotions = getEmotionsByCategories(this.config.emotionCategories as EmotionCategory[]);
    } else {
      emotions = getDefaultEnabledEmotions();
    }
    
    const emotion = emotions.find(e => e.type === reactionType);

    if (!emotion) return;

    // Update button appearance based on user reaction
    if (reactionData.userReacted) {
      btn.classList.add('active');
      btn.style.background = '#dbeafe';
      btn.style.borderColor = '#3b82f6';
    } else {
      btn.classList.remove('active');
      btn.style.background = '#f3f4f6';
      btn.style.borderColor = '#e5e7eb';
    }

    // Update count
    btn.textContent = `${emotion.emoji} ${reactionData.count}`;
  }

  /**
   * Update all article reaction buttons UI
   */
  private updateAllArticleReactionButtons(): void {
    // Determine which emotions to display based on configuration
    let emotions;
    if (this.config.availableEmotions && this.config.availableEmotions.length > 0) {
      emotions = getEmotionsByTypes(this.config.availableEmotions);
    } else if (this.config.emotionCategories && this.config.emotionCategories.length > 0) {
      emotions = getEmotionsByCategories(this.config.emotionCategories as EmotionCategory[]);
    } else {
      emotions = getDefaultEnabledEmotions();
    }
    
    emotions.forEach(emotion => {
      this.updateArticleReactionButton(emotion.type);
    });
  }

  private sortComments(comments: Comment[]): Comment[] {
    const sortBy = this.config.sortBy || 'newest';
    
    switch (sortBy) {
      case 'oldest':
        return comments.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      case 'newest':
      default:
        return comments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
  }

  private logout(): void {
    this.authManager.logout();
    this.currentUser = null;
    this.render();
  }

  /**
   * Initialize OAuth login for a specific provider
   */
  public async loginWithProvider(provider: string): Promise<void> {
    try {
      await this.authManager.login(provider);
      
      // Get updated auth state
      const authState = this.authManager.getState();
      if (authState.isAuthenticated && authState.user && authState.token) {
        // Convert WidgetUser to AuthUser format
        this.currentUser = {
          id: authState.user.id,
          displayName: authState.user.name,
          email: authState.user.email,
          avatarUrl: authState.user.avatar,
          isAnonymous: false
        };
        // Set token and user data in API service
        this.apiService.setToken(
          authState.token,
          authState.user.provider,
          authState.user.email,
          authState.user.name,
          authState.user.avatar
        );
        this.render();
      }
    } catch (error) {
      console.error('OAuth login failed:', error);
      this.showError('Authentication failed. Please try again.');
    }
  }

  /**
   * Check if user is currently authenticated
   */
  public isAuthenticated(): boolean {
    return this.authManager.getState().isAuthenticated;
  }

  /**
   * Get current user information
   */
  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Get available OAuth providers
   */
  public getAvailableProviders(): Array<{ id: string; name: string; icon: string }> {
    return this.authManager.getAvailableProviders();
  }

  private async submitComment(content: string): Promise<void> {
    try {
      // Create or get article first
      const article = await this.apiService.createOrGetArticle({
        url: this.config.articleUrl || window.location.href,
        title: this.config.articleTitle || document.title,
        projectId: this.config.projectId
      });

      const newComment = await this.apiService.createComment({
        projectId: this.config.projectId,
        articleId: article.id,
        content,
        parentId: undefined
      });

      // Ensure the comment has the current user as author and proper status
      if (!newComment.author && this.currentUser) {
        newComment.author = this.currentUser;
      }
      
      // If status is not set, assume it's pending for moderation
      if (!newComment.status) {
        newComment.status = 'Pending';
      }

      this.comments.unshift(newComment);
      this.render();

      // Clear form
      if (this.commentForm) {
        this.commentForm.clear();
      }

      // Show success message with moderation info if comment is pending
      if (newComment.status === 'Pending') {
        this.showSuccess('Your comment has been submitted and is awaiting moderation approval.');
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      this.showError('Failed to submit comment. Please try again.');
    }
  }

  private async submitReply(parentId: string, content: string): Promise<void> {
    try {
      const article = await this.apiService.createOrGetArticle({
        url: this.config.articleUrl || window.location.href,
        title: this.config.articleTitle || document.title,
        projectId: this.config.projectId
      });

      const reply = await this.apiService.createComment({
        projectId: this.config.projectId,
        articleId: article.id,
        content,
        parentId
      });

      // Ensure the reply has the current user as author and proper status
      if (!reply.author && this.currentUser) {
        reply.author = this.currentUser;
      }
      
      // If status is not set, assume it's pending for moderation
      if (!reply.status) {
        reply.status = 'Pending';
      }

      this.comments.push(reply);
      this.render();

      // Show success message with moderation info if reply is pending
      if (reply.status === 'Pending') {
        this.showSuccess('Your reply has been submitted and is awaiting moderation approval.');
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
      this.showError('Failed to submit reply. Please try again.');
    }
  }

  private async toggleReaction(commentId: string, type: string): Promise<void> {
    try {
      await this.apiService.toggleReaction(commentId, type, this.config.projectId);
      
      // Reload comments to get updated reaction counts
      await this.loadComments();
      this.render();
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  }

  private async editComment(commentId: string, content: string): Promise<void> {
    try {
      // Don't use loading state for edit - it's fast and causes UI issues
      // where all comments disappear if render() is called while isLoading=true
      const updatedComment = await this.apiService.updateComment(commentId, { content }, this.config.projectId);
      
      // Update comment in local array
      const index = this.comments.findIndex(c => c.id === commentId);

      if (index !== -1) {
        this.comments[index] = updatedComment;
        this.render();
      }
    } catch (error) {
      console.error('Error editing comment:', error);
      this.showError('Failed to edit comment. Please try again.');
    }
  }

  private async deleteComment(commentId: string): Promise<void> {
    // Note: Confirmation is handled by CommentList component before calling this method
    try {
      // Don't use loading state for delete - it's fast and causes UI issues
      // where all comments disappear if render() is called while isLoading=true
      await this.apiService.deleteComment(commentId, this.config.projectId);
      
      // Remove comment from local array
      this.comments = this.comments.filter(c => c.id !== commentId);
      this.render();
    } catch (error) {
      console.error('Error deleting comment:', error);
      this.showError('Failed to delete comment. Please try again.');
    }
  }

  private async handleCommentStarRatingSubmit(commentId: string, rating: number): Promise<void> {
    try {
      const response = await this.apiService.submitCommentStarRating(commentId, rating, this.config.projectId);
      
      // Update comment in local array with new star rating data
      const comment = this.comments.find(c => c.id === commentId);

      if (comment) {
        comment.starRating = {
          average: response.averageRating,
          count: response.totalCount,
          distribution: response.distribution,
          userRating: rating
        };
        this.render();
        this.showSuccess('Star rating submitted successfully!');
      }
    } catch (error) {
      console.error('Error submitting comment star rating:', error);
      this.showError('Failed to submit star rating. Please try again.');
    }
  }

  private async handleCommentStarRatingClear(commentId: string): Promise<void> {
    try {
      const response = await this.apiService.deleteCommentStarRating(commentId, this.config.projectId);
      
      // Update comment in local array - remove user rating but keep stats
      const comment = this.comments.find(c => c.id === commentId);

      if (comment && comment.starRating) {
        comment.starRating = {
          average: response.averageRating,
          count: response.totalCount,
          distribution: response.distribution,
          userRating: null
        };
        this.render();
        this.showSuccess('Star rating cleared successfully!');
      }
    } catch (error) {
      console.error('Error clearing comment star rating:', error);
      this.showError('Failed to clear star rating. Please try again.');
    }
  }

  private async loadCommentStarRatings(): Promise<void> {
    console.log('[CommentStarRatings] Starting to load star ratings for comments');
    console.log('[CommentStarRatings] Config enableCommentStarRatings:', this.config.enableCommentStarRatings);
    console.log('[CommentStarRatings] Number of comments:', this.comments.length);
    console.log('[CommentStarRatings] Project ID:', this.config.projectId);

    if (!this.config.enableCommentStarRatings) {
      console.log('[CommentStarRatings] Feature is disabled in config, skipping');
      return;
    }

    if (this.comments.length === 0) {
      console.log('[CommentStarRatings] No comments to load ratings for');
      return;
    }

    // Load star ratings for all comments in parallel
    const loadPromises = this.comments.map(async (comment) => {
      try {
        console.log(`[CommentStarRatings] Loading rating for comment ${comment.id}`);
        const starRating = await this.apiService.getCommentStarRating(comment.id, this.config.projectId);
        
        console.log(`[CommentStarRatings] Received rating data for comment ${comment.id}:`, starRating);
        
        // Update comment with star rating data
        comment.starRating = {
          average: starRating.averageRating,
          count: starRating.totalCount,
          distribution: starRating.distribution,
          userRating: starRating.userRating
        };

        console.log(`[CommentStarRatings] Updated comment ${comment.id} with starRating:`, comment.starRating);
      } catch (error) {
        console.error(`[CommentStarRatings] Error loading star rating for comment ${comment.id}:`, error);
        // Continue with other comments even if one fails
      }
    });

    // Wait for all star rating loads to complete
    await Promise.all(loadPromises);
    
    console.log('[CommentStarRatings] Finished loading all star ratings');
    console.log('[CommentStarRatings] Comments with starRating data:', 
      this.comments.filter(c => c.starRating).length);
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    
    // Disable forms during loading
    const forms = this.container.querySelectorAll('form');
    forms.forEach(form => {
      const inputs = form.querySelectorAll('input, textarea, button');
      inputs.forEach(input => {
        (input as HTMLInputElement).disabled = loading;
      });
    });
  }

  private showError(message: string): void {
    // Remove existing error/success messages
    const existingError = this.container.querySelector('.komentas-error');
    const existingSuccess = this.container.querySelector('.komentas-success');

    if (existingError) {
      existingError.remove();
    }
    if (existingSuccess) {
      existingSuccess.remove();
    }

    // Add new error
    const error = createElement('div', { className: 'komentas-error' }, [message]);
    const authSection = this.container.querySelector('.komentas-auth-section');

    if (authSection) {
      this.container.insertBefore(error, authSection);
    } else {
      this.container.appendChild(error);
    }

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (error.parentNode) {
        error.parentNode.removeChild(error);
      }
    }, 5000);
  }

  private showSuccess(message: string): void {
    // Remove existing error/success messages
    const existingError = this.container.querySelector('.komentas-error');
    const existingSuccess = this.container.querySelector('.komentas-success');

    if (existingError) {
      existingError.remove();
    }
    if (existingSuccess) {
      existingSuccess.remove();
    }

    // Add new success message
    const success = createElement('div', { className: 'komentas-success' }, [message]);
    const authSection = this.container.querySelector('.komentas-auth-section');

    if (authSection) {
      this.container.insertBefore(success, authSection);
    } else {
      this.container.appendChild(success);
    }

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (success.parentNode) {
        success.parentNode.removeChild(success);
      }
    }, 5000);
  }

  // Public methods
  public async refresh(): Promise<void> {
    await this.loadComments();
    this.debouncedRender();
  }

  /**
   * Set up intersection observer for lazy loading images
   */
  private setupIntersectionObserver(): void {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            this.intersectionObserver?.unobserve(img);
          }
        }
      });
    }, {
      rootMargin: '50px',
      threshold: 0.1
    });
  }

  private render(): void {
    // Use optimized render for better performance when possible
    if (this.comments.length > 20) {
      this.renderOptimized();
    } else {
      this.updateHeader();
      this.renderAuth();
      this.renderComments();
    }
  }

  /**
   * Optimized render method with caching
   */
  private renderOptimized(): void {
    if (!this.commentList) return;

    // Use cached elements when possible
    const commentsContainer = this.container.querySelector('.komentas-comments-list');
    if (!commentsContainer) return;

    // Clear container
    commentsContainer.innerHTML = '';

    // Render comments with caching
    this.comments.forEach((comment) => {
      const cacheKey = `${comment.id}-${comment.updatedAt}`;
      let commentElement = this.commentCache.get(cacheKey);

      if (!commentElement) {
        commentElement = this.createOptimizedCommentElement(comment);
        this.commentCache.set(cacheKey, commentElement);
      } else {
        // Clone cached element
        commentElement = commentElement.cloneNode(true) as HTMLElement;
      }

      commentsContainer.appendChild(commentElement);
    });
  }

  /**
   * Create optimized comment element with lazy loading
   */
  private createOptimizedCommentElement(comment: Comment): HTMLElement {
    const commentEl = createElement('div', { 
      className: 'komentas-comment',
      'data-comment-id': comment.id 
    });

    // Use lazy loading for avatar images
    const avatarSrc = comment.author.avatar || 
      `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.author.displayName)}&size=32`;

    commentEl.innerHTML = `
      <div class="komentas-comment-header">
        <img class="komentas-comment-avatar" 
             data-src="${avatarSrc}"
             alt="${comment.author.displayName}"
             style="background: #f0f0f0; width: 32px; height: 32px;">
        <div class="komentas-author-info">
          <span class="komentas-author-name">${comment.author.displayName}</span>
          <span class="komentas-comment-time">${this.formatDate(comment.createdAt)}</span>
        </div>
      </div>
      <div class="komentas-comment-content">${this.sanitizeContent(comment.content)}</div>
    `;

    // Set up lazy loading for avatar
    const avatar = commentEl.querySelector('.komentas-comment-avatar') as HTMLImageElement;
    if (avatar && this.intersectionObserver) {
      this.intersectionObserver.observe(avatar);
    }

    return commentEl;
  }

  /**
   * Utility methods for content processing
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

      if (diffInHours < 1) {
        return 'Just now';
      } else if (diffInHours < 24) {
        return `${Math.floor(diffInHours)} hours ago`;
      } else if (diffInHours < 24 * 7) {
        return `${Math.floor(diffInHours / 24)} days ago`;
      } else {
        return date.toLocaleDateString();
      }
    } catch {
      return 'Unknown time';
    }
  }

  private sanitizeContent(content: string): string {
    // Basic HTML sanitization - replace with proper sanitizer in production
    return content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\n/g, '<br>');
  }

  /**
   * Clean up resources when widget is destroyed
   */
  public destroy(): void {
    // Disconnect observers
    this.lazyLoader.disconnect();
    this.intersectionObserver?.disconnect();
    
    // Clear caches
    this.commentCache.clear();
    
    // Clear container
    this.container.innerHTML = '';
    
    // Remove styles
    const styles = document.getElementById('komentas-widget-styles');
    if (styles) {
      styles.remove();
    }
  }

  public setTheme(theme: 'light' | 'dark'): void {
    this.isDarkTheme = theme === 'dark';
    this.addStyles(); // Re-add styles with new theme
    this.debouncedRender();
  }
}